import React, { useState, lazy } from 'react';
import { ExternalLinkIcon } from 'lucide-react';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
function ImacFrame({
  image,
  title,
  url,
  category
}: {
  image: string;
  title: string;
  url: string;
  category?: string;
}) {
  const displayUrl = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const [imgError, setImgError] = useState(false);
  const showFallback = !image || imgError;
  const initials = title.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  return <div className="flex flex-col items-center" data-id="element-1154" data-mp="15wj6l7fancup">
      {/* iMac Body */}
      <div className="w-full rounded-xl overflow-hidden border border-white/10 bg-[#1a1a1a] shadow-2xl group-hover:shadow-[0_20px_60px_rgba(212,175,55,0.15)] transition-shadow duration-500" data-id="element-1155" data-mp="1av2p0xuoi6ib">
        {/* Top Bar - macOS style */}
        <div className="flex items-center gap-2 px-3 py-2 bg-[#2a2a2a] border-b border-white/5" data-id="element-1156" data-mp="1jw0yvwrst88t">
          <div className="flex items-center gap-1.5" data-id="element-1157" data-mp="37swbvpmbdx80">
            <div className="w-2 h-2 rounded-full bg-[#ff5f57]" data-id="element-1158" data-mp="33qrm51yzlkqn" />
            <div className="w-2 h-2 rounded-full bg-[#febc2e]" data-id="element-1159" data-mp="33p85vbdbz9ov" />
            <div className="w-2 h-2 rounded-full bg-[#28c840]" data-id="element-1160" data-mp="2yshqyrd9ik51" />
          </div>
          <div className="flex-1 mx-3" data-id="element-1161" data-mp="2tsvckelp6xun">
            <div className="bg-[#1a1a1a] rounded-md px-2 py-0.5 text-[9px] text-gray-500 text-center truncate font-mono" data-id="element-1162" data-mp="2ttwqqry0g0gl">
              {displayUrl}
            </div>
          </div>
        </div>

        {/* Screen */}
        <div className="relative overflow-hidden aspect-[16/10] bg-black" data-id="element-1163" data-mp="3mj3xge7mbwcx">
          {showFallback ? <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#1a1a1a] via-black to-[#0d0d0d]" data-id="element-1164" data-mp="3hny1emki51b4">
              <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay" data-id="element-1165" data-mp="e1hcmqq5ccr1" />
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-[60px]" data-id="element-1166" data-mp="9969zg40ucus" />
              <div className="relative z-10 flex flex-col items-center text-center px-6" data-id="element-1167" data-mp="23kfrvjeq99tt">
                <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-4" data-id="element-1168" data-mp="27j50yzsqg57n">
                  <span className="text-2xl font-bold text-[#D4AF37]" data-id="element-1169" data-mp="1yrcxj924lwck">
                    {initials}
                  </span>
                </div>
                <p className="text-white font-bold text-lg leading-tight" data-id="element-1170" data-mp="1jssnzaimria7">
                  {title}
                </p>
                {category && <p className="text-gray-500 text-xs uppercase tracking-widest mt-2" data-id="element-1171" data-mp="1au77dkwpydc2">
                    {category}
                  </p>}
              </div>
            </div> : <img src={image} alt={`${title} website preview`} loading="lazy" onError={() => setImgError(true)} className="w-full h-auto object-cover object-top absolute top-0 left-0 transition-transform duration-[4000ms] ease-in-out group-hover:-translate-y-[60%]" data-id="element-1172" data-mp="357f6vpvn3d41" />}
          {/* Subtle overlay */}
          <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500 z-10" data-id="element-1173" data-mp="3t3jy2kbjnenr" />

          {/* Hover visit icon */}
          <div className="absolute inset-0 flex items-center justify-center z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-1174" data-mp="3oa77w5r7uzsw">
            <div className="w-12 h-12 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center border border-white/20 shadow-lg" data-id="element-1175" data-mp="1vxl212xpzu8">
              <ExternalLinkIcon className="w-5 h-5 text-white" data-id="element-1176" data-mp="buwx7tmwsdlz" />
            </div>
          </div>

          {/* Live badge */}
          <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-[10px] font-medium text-gray-300" data-id="element-1177" data-mp="2hn3csadb63sz">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-1178" data-mp="2f48de4pyrp4l" /> Live
          </div>
        </div>
      </div>

      {/* iMac Stand */}
      <div className="relative flex flex-col items-center" data-id="element-1179" data-mp="2k0p14xt941h5">
        <div className="w-12 h-5 bg-gradient-to-b from-[#c0c0c0] to-[#a0a0a0]" style={{
        clipPath: 'polygon(15% 0%, 85% 0%, 100% 100%, 0% 100%)'
      }} data-id="element-1180" data-mp="2o3vgqw29750a" />
        <div className="w-20 h-1.5 bg-gradient-to-b from-[#b0b0b0] to-[#909090] rounded-full -mt-px" data-id="element-1181" data-mp="1wdqoycc5zfp1" />
      </div>
    </div>;
}
export function PortfolioPage() {
  usePageSEO({
    canonical: '/portfolio',
    keywords: 'Shopify portfolio, Shopify examples, custom Shopify stores, ecommerce portfolio, Shopify agency work',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Portfolio',
      path: '/portfolio'
    }]),
    title: 'Portfolio | Design Flow Digitals',
    description: 'Browse our portfolio of premium Shopify stores. Design Flow Digitals has built custom ecommerce experiences for skincare, fitness, wellness, and home brands worldwide.'
  });
  const [activeFilter, setActiveFilter] = useState('All');
  const filters = ['All', 'Beauty', 'Health & Fitness', 'Home & Living', 'Wellness', 'Food & Beverage', 'Fashion', 'Collectibles', 'Travel', 'Education', 'Apps'];
  const projects = [{
    title: 'Art with Mrs E',
    category: 'Art Education',
    filterTag: 'Education',
    desc: 'Art education and teaching resources brand. Friendly, vibrant Shopify store with organized digital product catalogs, clear curriculum storytelling, and an approachable, creative aesthetic.',
    url: 'https://artwithmrse.com/',
    image: 'https://api.microlink.io/?url=https%3A%2F%2Fartwithmrse.com%2F&screenshot=true&embed=screenshot.url&meta=false&waitUntil=networkidle2&viewport.width=1280&viewport.height=2400'
  }, {
    title: 'La Sicilyana Wines',
    category: 'Wine & Spirits',
    filterTag: 'Food & Beverage',
    desc: 'Sicilian wine brand. Elegant, heritage-driven Shopify store with refined product photography, regional storytelling, and a sophisticated, premium aesthetic.',
    url: 'https://lasicilyanawines.co.uk/',
    image: 'https://api.microlink.io/?url=https%3A%2F%2Flasicilyanawines.co.uk%2F&screenshot=true&embed=screenshot.url&meta=false&waitUntil=networkidle2&viewport.width=1280&viewport.height=2400'
  }, {
    title: 'Head of Clouds',
    category: 'Home Fragrance',
    filterTag: 'Home & Living',
    desc: 'Premium candle and home fragrance brand. Atmospheric, sensory-driven Shopify store with rich product imagery, scent storytelling, and a calm, elevated aesthetic.',
    url: 'https://headofclouds.com/',
    image: 'https://api.microlink.io/?url=https%3A%2F%2Fheadofclouds.com%2F&screenshot=true&embed=screenshot.url&meta=false&waitUntil=networkidle2&viewport.width=1280&viewport.height=2400'
  }, {
    title: 'SEO Suite',
    category: 'Shopify App',
    filterTag: 'Apps',
    desc: 'Automated Shopify SEO & Rich Snippets app. Comprehensive Shopify app for meta tag automation, image optimization, JSON-LD schema, and technical SEO fixes to boost organic traffic.',
    url: 'https://apps.shopify.com/seo-suite',
    image: 'https://api.microlink.io/?url=https%3A%2F%2Fdesignflowdigitals.com%2Fseo-suite&screenshot=true&embed=screenshot.url&meta=false&waitUntil=networkidle2&viewport.width=1280&viewport.height=2400'
  }, {
    title: 'AI Product Photoshoot',
    category: 'Shopify App',
    filterTag: 'Apps',
    desc: 'Studio-quality product scenes powered by AI. Transform plain product shots into high-converting lifestyle scenes in seconds using artificial intelligence.',
    url: 'https://apps.shopify.com/ai-product-photoshoot-dfd',
    image: 'https://api.microlink.io/?url=https%3A%2F%2Fdesignflowdigitals.com%2Fai-photoshoot&screenshot=true&embed=screenshot.url&meta=false&waitUntil=networkidle2&viewport.width=1280&viewport.height=2400'
  }, {
    title: 'The Label House Collection',
    category: 'Fashion & Apparel',
    filterTag: 'Fashion',
    desc: 'Contemporary fashion and apparel brand. Polished, lookbook-driven Shopify store with editorial product photography, seasonal collection storytelling, and a refined, modern aesthetic.',
    url: 'https://www.thelabelhousecollection.com.au/',
    image: 'https://api.microlink.io/?url=https%3A%2F%2Fwww.thelabelhousecollection.com.au%2F&screenshot=true&embed=screenshot.url&meta=false&waitUntil=networkidle2&viewport.width=1280&viewport.height=2400'
  }, {
    title: 'EasyMirror',
    category: 'Smart Mirrors',
    filterTag: 'Home & Living',
    desc: 'Smart LED mirror brand. Sleek, modern Shopify store with crisp product visuals, feature-focused detail pages, and a minimal, premium aesthetic.',
    url: 'https://easymirror.de/',
    image: 'https://api.microlink.io/?url=https%3A%2F%2Feasymirror.de%2F&screenshot=true&embed=screenshot.url&meta=false&waitUntil=networkidle2&viewport.width=1280&viewport.height=2400'
  }, {
    title: 'ZAP',
    category: 'Cold Brew Energy',
    filterTag: 'Food & Beverage',
    desc: 'Bold cold brew energy drink brand, designed for Almost Perfect Themes. High-energy, vibrant Shopify store with dynamic product showcases, flavor storytelling, and a punchy, modern aesthetic.',
    url: 'https://zap-main.myshopify.com/',
    image: 'https://cdn.magicpatterns.com/uploads/uzHhtQoRAGMq1c46BKQ6g9/screencapture-zap-main-myshopify-2026-06-21-01_09_06.png'
  }, {
    title: 'Girl and a Plane Guides',
    category: 'Travel Guides',
    filterTag: 'Travel',
    desc: 'Curated travel guides and itineraries brand. Inspiring, wanderlust-driven Shopify store with immersive imagery, destination storytelling, and a clean, editorial aesthetic.',
    url: 'https://girlandaplaneguides.com/',
    image: 'https://api.microlink.io/?url=https%3A%2F%2Fgirlandaplaneguides.com%2F&screenshot=true&embed=screenshot.url&meta=false&waitUntil=networkidle2&viewport.width=1280&viewport.height=2400'
  }, {
    title: 'Mama Said',
    category: 'Natural Skincare',
    filterTag: 'Beauty',
    desc: 'Clean, natural tallow-based skincare. Custom Shopify store with warm, inviting design and product-focused layouts.',
    url: 'https://mamasaid.shop/',
    image: 'https://cdn.magicpatterns.com/uploads/gge3zN3o5yhdVPojCK8gy1/screencapture-mamasaid-shop-2026-03-17-02_13_46.png'
  }, {
    title: 'Raptor Gym',
    category: 'Fitness Supplements',
    filterTag: 'Health & Fitness',
    desc: 'High-performance supplement brand with bold, dark aesthetic. Custom product pages and ingredient transparency features.',
    url: 'https://www.raptorgym.co.uk/',
    image: 'https://cdn.magicpatterns.com/uploads/ikbA1fqtaom6LKDzA2LgpA/screencapture-raptorgym-co-uk-2026-03-17-02_14_32.png'
  }, {
    title: 'Satin Gel',
    category: 'Personal Wellness',
    filterTag: 'Wellness',
    desc: 'Plant-based intimate wellness brand. Clean, modern Shopify store with premium product photography and trust-building design.',
    url: 'https://satingel.com/',
    image: 'https://cdn.magicpatterns.com/uploads/ds7DHKaHoPCS354XeKHhma/screencapture-satingel-2026-03-17-02_17_21.png'
  }, {
    title: 'Palmpring USA',
    category: 'Organic Home',
    filterTag: 'Home & Living',
    desc: 'Organic mattress and bedding brand. Natural, eco-focused design with detailed product specs and healthy materials education.',
    url: 'https://palmpringusa.com/',
    image: 'https://cdn.magicpatterns.com/uploads/54xdsG51LqKkCannc3hvSD/screencapture-palmpringusa-2026-03-17-02_16_18.png'
  }, {
    title: '4BiddenKnowledge',
    category: 'Esoteric & Lifestyle',
    filterTag: 'Fashion',
    desc: 'Esoteric knowledge and lifestyle brand by Billy Carson. Bold, immersive Shopify store featuring books, apparel, wellness products, and conscious thought merchandise.',
    url: 'https://4biddenknowledge.shop/',
    image: 'https://cdn.magicpatterns.com/uploads/h57ohLRzA3DQqr77MCoEwu/screencapture-4biddenknowledge-shop-2026-03-22-23_44_11.png'
  }, {
    title: 'Twig Story',
    category: 'Handcrafted Home Decor',
    filterTag: 'Home & Living',
    desc: 'Handmade and curated home products featuring beautiful hand-painted bowls, trays, and serveware. Warm, artisan-focused Shopify store.',
    url: 'https://twigstory.com/',
    image: 'https://cdn.magicpatterns.com/uploads/qJygmKeABjHLSmYCsKfSX6/screencapture-twigstory-2026-03-17-20_41_11_(1).png'
  }, {
    title: 'Mawka Drink',
    category: 'Food & Beverage',
    filterTag: 'Food & Beverage',
    desc: 'Caffeine-free coffee alternative brand. Warm, lifestyle-driven Shopify store with rich product storytelling and sustainable brand messaging.',
    url: 'https://mawkadrink.com/',
    image: 'https://cdn.magicpatterns.com/uploads/g1QDmrVN1X9UdEsGoqGqa3/screencapture-mawkadrink-2026-03-17-20_51_34.png'
  }, {
    title: 'Casa Veve',
    category: 'Fashion & Apparel',
    filterTag: 'Fashion',
    desc: 'Premium loungewear and sleepwear brand. Elegant Shopify store with curated collections, seasonal lookbooks, and worldwide shipping.',
    url: 'https://casaveve.com/',
    image: 'https://cdn.magicpatterns.com/uploads/wA4U4WdYdQP3rYZCJqYfvN/screencapture-casaveve-2026-03-17-21_00_36.png'
  }, {
    title: 'UpCup Koffee',
    category: 'Functional Coffee',
    filterTag: 'Food & Beverage',
    desc: "Cognitive mushroom coffee brand with NAD+, Lion's Mane, and Shilajit. Bold, science-driven Shopify store with rich ingredient education and clean product storytelling.",
    url: 'https://upcupkoffee.com/',
    image: 'https://cdn.magicpatterns.com/uploads/xxwtWZ8HikKQw1DFY8zoq2/screencapture-upcupkoffee-2026-03-23-00_12_01.png'
  }, {
    title: 'Three Happy Coconuts',
    category: 'Coconut Food & Beverage',
    filterTag: 'Food & Beverage',
    desc: 'Tropical coconut-based food and beverage brand. Vibrant, lifestyle-driven Shopify store with rich product storytelling, clean layouts, and a warm, natural aesthetic.',
    url: 'https://threehappycoconuts.com/',
    image: 'https://api.microlink.io/?url=https%3A%2F%2Fthreehappycoconuts.com%2F&screenshot=true&embed=screenshot.url&meta=false&waitUntil=networkidle2&viewport.width=1280&viewport.height=2400'
  }, {
    title: 'MZL Diecasts',
    category: 'Diecast Model Cars',
    filterTag: 'Collectibles',
    desc: 'Premium diecast model car and collectibles brand. Bold, enthusiast-focused Shopify store with detailed product galleries, collection storytelling, and a sleek, high-end aesthetic.',
    url: 'https://mzldiecasts.com/',
    image: 'https://cdn.magicpatterns.com/uploads/qYdHZAuXwiuKEgSXpjsA4f/screencapture-mzldiecasts-2026-06-19-23_35_28.png'
  }, {
    title: 'Nemali Silver Jewelry',
    category: 'Fine Silver Jewelry',
    filterTag: 'Fashion',
    desc: 'Handcrafted silver jewelry brand. Elegant, detail-rich Shopify store with refined product photography, collection storytelling, and a timeless, luxurious aesthetic.',
    url: 'https://nemalisilverjewelry.com/',
    image: 'https://api.microlink.io/?url=https%3A%2F%2Fnemalisilverjewelry.com%2F&screenshot=true&embed=screenshot.url&meta=false&waitUntil=networkidle2&viewport.width=1280&viewport.height=2400'
  }];
  const filteredProjects = activeFilter === 'All' ? projects : projects.filter(p => p.filterTag === activeFilter);
  return <div className="pt-32 pb-20" data-id="element-1182" data-mp="23syryl2j7s81">
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-16" data-id="element-1183" data-mp="23s6wrbcujwia">
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-1184" data-mp="1yuniwck29lqv">Our Portfolio</h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed" data-id="element-1185" data-mp="1ttzqbmg6owuj">
          Over 1,000 Shopify stores designed and developed for brands worldwide.
          Here are some of our favorites.
        </p>
      </section>

      <section className="section-padding pt-0" data-id="element-1186" data-mp="1kumi0x3sqebe">
        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-16" data-id="element-1187" data-mp="1g2maaihhih49">
          {filters.map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${activeFilter === filter ? 'bg-[#D4AF37] text-black shadow-[0_0_15px_rgba(212,175,55,0.3)]' : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'}`} data-id="element-1188" data-mp="1b39rc8tqhu6y">
              {filter}
            </button>)}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1189" data-mp="3dqoq0nlrzp3t">
          {filteredProjects.map(project => <a key={project.title} href={project.url} target="_blank" rel="noopener noreferrer" className="group block" data-id="element-1190" data-mp="3hs1yl5g966ef">
              <ImacFrame image={project.image} title={project.title} url={project.url} category={project.category} data-id="element-1191" data-mp="2q25jacidjkdu" />
              <div className="text-center mt-5" data-id="element-1192" data-mp="2z1kaxfaa20zx">
                <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider mb-2" data-id="element-1193" data-mp="2yzpotxn9s2u9">
                  {project.category}
                </p>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors" data-id="element-1194" data-mp="26c3jokye8q5a">
                  {project.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed max-w-md mx-auto" data-id="element-1195" data-mp="14oniidn1p8si">
                  {project.desc}
                </p>
              </div>
            </a>)}
        </div>

        {/* Empty State */}
        {filteredProjects.length === 0 && <div className="text-center py-20" data-id="element-1196" data-mp="1dnhbzv52xj1u">
            <p className="text-gray-500 text-lg" data-id="element-1197" data-mp="19nqnxz6xth10">
              No projects found for this category.
            </p>
            <button onClick={() => setActiveFilter('All')} className="mt-4 text-[#D4AF37] font-medium hover:underline" data-id="element-1198" data-mp="1nmgmlj9enorh">
              View all projects
            </button>
          </div>}

        {/* Highlights Note */}
        <div className="mt-24 glass-card p-10 text-center max-w-3xl mx-auto border-[#D4AF37]/20" data-id="element-1199" data-mp="1sdyc9b5za2x5">
          <h3 className="text-xl font-bold text-white mb-6" data-id="element-1200" data-mp="26dpvbqbcq9i0">
            Every project highlights our core strengths:
          </h3>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 text-gray-300 font-medium" data-id="element-1201" data-mp="21gh9nfqzvnc0">
            <span className="flex items-center gap-2" data-id="element-1202" data-mp="2l29d0yphq0os">
              <div className="w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_8px_#D4AF37]" data-id="element-1203" data-mp="2pzssu31u6liu" />{' '}
              Design Strategy
            </span>
            <span className="flex items-center gap-2" data-id="element-1204" data-mp="2bzqegqsqma8c">
              <div className="w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_8px_#D4AF37]" data-id="element-1205" data-mp="2g1lii321z1pt" />{' '}
              Development Approach
            </span>
            <span className="flex items-center gap-2" data-id="element-1206" data-mp="2295jreg6ppup">
              <div className="w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_8px_#D4AF37]" data-id="element-1207" data-mp="271oywfuist1m" />{' '}
              Performance Results
            </span>
          </div>
        </div>
      </section>
    </div>;
}